import React from 'react';
import {
  BuildingOfficeIcon,
  UserGroupIcon,
  UserCircleIcon,
  EnvelopeIcon,
  MapPinIcon,
  UsersIcon
} from '@heroicons/react/24/outline';

export default function OrganizationTreeHelp() {
  return (
    <div className="max-w-4xl mx-auto p-6 bg-gradient-to-br from-slate-50 to-indigo-50 rounded-lg shadow-lg">
      <div className="text-center mb-8">
        <BuildingOfficeIcon className="mx-auto h-16 w-16 text-indigo-600 mb-4" />
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Organization Structure</h1>
        <p className="text-lg text-gray-600">Visual representation of company hierarchy, departments, and team members</p>
      </div>

      {/* Overview Section */}
      <div className="bg-white rounded-lg p-6 mb-6 shadow-md">
        <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
          <UsersIcon className="h-6 w-6 text-indigo-600 mr-2" />
          Overview
        </h2>
        <p className="text-gray-700 mb-4">
          The Organization Structure view provides a comprehensive visual representation of your company's hierarchy,
          departments, and team members. This interactive dashboard helps you understand team composition, departmental
          organization, and facilitates communication between staff members across different departments.
        </p>
        <div className="bg-indigo-50 border-l-4 border-indigo-400 p-4">
          <p className="text-sm text-indigo-700">
            <strong>Key Features:</strong> Company overview, departmental breakdown, staff profiles, direct messaging, and organizational insights.
          </p>
        </div>
      </div>

      {/* Interface Layout */}
      <div className="bg-white rounded-lg p-6 mb-6 shadow-md">
        <h2 className="text-2xl font-semibold text-gray-900 mb-4">Interface Overview</h2>

        {/* Company Header */}
        <div className="mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-3">Company Overview Header</h3>
          <div className="bg-gradient-to-r from-gray-900/80 via-blue-900/50 to-gray-900/80 rounded-2xl shadow-xl border border-white/20 p-4">
            <div className="text-center">
              <h1 className="text-xl font-bold text-white mb-2 flex items-center justify-center">
                <span className="text-2xl mr-2">🏢</span>
                TechCorp Solutions
              </h1>
              <p className="text-sm text-blue-200">Your organization at a glance</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl px-4 py-3 border border-white/20">
              <div className="flex items-center">
                <MapPinIcon className="h-5 w-5 text-emerald-400 mr-2" />
                <div>
                  <p className="text-xs text-blue-200 font-medium">Location</p>
                  <p className="text-sm font-semibold text-white opacity-75 cursor-not-allowed">London, UK</p>
                </div>
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl px-4 py-3 border border-white/20">
              <div className="flex items-center">
                <span className="text-lg mr-2">👥</span>
                <div>
                  <p className="text-xs text-blue-200 font-medium">Total Staff</p>
                  <p className="text-sm font-semibold text-white">47</p>
                </div>
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl px-4 py-3 border border-white/20">
              <div className="flex items-center">
                <span className="text-lg mr-2">🏬</span>
                <div>
                  <p className="text-xs text-blue-200 font-medium">Departments</p>
                  <p className="text-sm font-semibold text-white">5</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Department Structure */}
        <div className="mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-3">Department Breakdown</h3>
          <div className="space-y-4">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 p-4">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                  <span className="text-xl mr-3">🏬</span>
                  <div>
                    <h3 className="text-lg font-bold text-cyan-300">Engineering</h3>
                    <p className="text-xs text-blue-200">12 team members</p>
                  </div>
                </div>
                <div className="bg-cyan-400/20 backdrop-blur-sm rounded-full px-3 py-1 border border-cyan-400/30">
                  <span className="text-sm font-semibold text-cyan-300">12</span>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 hover:border-cyan-400/50 transition-all duration-200">
                  <div className="flex flex-col items-center text-center">
                    <div className="relative mb-3">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center border-3 border-cyan-300 shadow-lg">
                        <UserCircleIcon className="h-10 w-10 text-white" />
                      </div>
                      <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-emerald-500 rounded-full border-2 border-white flex items-center justify-center">
                        <span className="text-xs text-white font-bold">✓</span>
                      </div>
                    </div>

                    <h4 className="font-bold text-white text-sm mb-1 line-clamp-1">John Smith</h4>
                    <p className="text-xs text-cyan-200 mb-3 font-medium">Senior Developer</p>

                    <button className="flex-1 bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white text-xs font-semibold py-2 px-3 rounded-lg transition-all duration-200 active:scale-95 touch-manipulation flex items-center justify-center opacity-75 cursor-not-allowed">
                      <EnvelopeIcon className="h-4 w-4 mr-1" />
                      Message
                    </button>
                  </div>
                </div>

                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 hover:border-cyan-400/50 transition-all duration-200">
                  <div className="flex flex-col items-center text-center">
                    <div className="relative mb-3">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center border-3 border-cyan-300 shadow-lg">
                        <UserCircleIcon className="h-10 w-10 text-white" />
                      </div>
                      <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-emerald-500 rounded-full border-2 border-white flex items-center justify-center">
                        <span className="text-xs text-white font-bold">✓</span>
                      </div>
                    </div>

                    <h4 className="font-bold text-white text-sm mb-1 line-clamp-1">Sarah Johnson</h4>
                    <p className="text-xs text-cyan-200 mb-3 font-medium">Frontend Developer</p>

                    <button className="flex-1 bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white text-xs font-semibold py-2 px-3 rounded-lg transition-all duration-200 active:scale-95 touch-manipulation flex items-center justify-center opacity-75 cursor-not-allowed">
                      <EnvelopeIcon className="h-4 w-4 mr-1" />
                      Message
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 p-4">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                  <span className="text-xl mr-3">🏬</span>
                  <div>
                    <h3 className="text-lg font-bold text-cyan-300">Sales</h3>
                    <p className="text-xs text-blue-200">8 team members</p>
                  </div>
                </div>
                <div className="bg-cyan-400/20 backdrop-blur-sm rounded-full px-3 py-1 border border-cyan-400/30">
                  <span className="text-sm font-semibold text-cyan-300">8</span>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 hover:border-cyan-400/50 transition-all duration-200">
                  <div className="flex flex-col items-center text-center">
                    <div className="relative mb-3">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center border-3 border-cyan-300 shadow-lg">
                        <UserCircleIcon className="h-10 w-10 text-white" />
                      </div>
                      <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-emerald-500 rounded-full border-2 border-white flex items-center justify-center">
                        <span className="text-xs text-white font-bold">✓</span>
                      </div>
                    </div>

                    <h4 className="font-bold text-white text-sm mb-1 line-clamp-1">Mike Wilson</h4>
                    <p className="text-xs text-cyan-200 mb-3 font-medium">Sales Manager</p>

                    <button className="flex-1 bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white text-xs font-semibold py-2 px-3 rounded-lg transition-all duration-200 active:scale-95 touch-manipulation flex items-center justify-center opacity-75 cursor-not-allowed">
                      <EnvelopeIcon className="h-4 w-4 mr-1" />
                      Message
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Key Features */}
      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-lg p-6 shadow-md">
          <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
            <BuildingOfficeIcon className="h-5 w-5 text-indigo-600 mr-2" />
            Company Overview
          </h3>
          <ul className="space-y-2 text-gray-700">
            <li className="flex items-start">
              <span className="text-indigo-500 mr-2">•</span>
              Complete company information display
            </li>
            <li className="flex items-start">
              <span className="text-indigo-500 mr-2">•</span>
              Location and contact details
            </li>
            <li className="flex items-start">
              <span className="text-indigo-500 mr-2">•</span>
              Total staff and department counts
            </li>
            <li className="flex items-start">
              <span className="text-indigo-500 mr-2">•</span>
              Organizational statistics at a glance
            </li>
          </ul>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-md">
          <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
            <UserGroupIcon className="h-5 w-5 text-cyan-600 mr-2" />
            Department Management
          </h3>
          <ul className="space-y-2 text-gray-700">
            <li className="flex items-start">
              <span className="text-cyan-500 mr-2">•</span>
              Hierarchical department structure
            </li>
            <li className="flex items-start">
              <span className="text-cyan-500 mr-2">•</span>
              Staff member counts per department
            </li>
            <li className="flex items-start">
              <span className="text-cyan-500 mr-2">•</span>
              Visual team composition overview
            </li>
            <li className="flex items-start">
              <span className="text-cyan-500 mr-2">•</span>
              Department-specific insights
            </li>
          </ul>
        </div>
      </div>

      {/* Staff Profiles */}
      <div className="bg-white rounded-lg p-6 mb-6 shadow-md">
        <h2 className="text-2xl font-semibold text-gray-900 mb-4">Staff Profile Cards</h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-3">Profile Information</h3>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                Profile pictures with fallback avatars
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                Full name and job title display
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                Online status indicators
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                Department assignment visibility
              </li>
            </ul>
          </div>
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-3">Communication Features</h3>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start">
                <span className="text-blue-500 mr-2">•</span>
                Direct messaging capability
              </li>
              <li className="flex items-start">
                <span className="text-blue-500 mr-2">•</span>
                Quick communication buttons
              </li>
              <li className="flex items-start">
                <span className="text-blue-500 mr-2">•</span>
                Cross-department collaboration
              </li>
              <li className="flex items-start">
                <span className="text-blue-500 mr-2">•</span>
                Team coordination tools
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Usage Guidelines */}
      <div className="bg-white rounded-lg p-6 shadow-md">
        <h2 className="text-2xl font-semibold text-gray-900 mb-4">Usage Guidelines</h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-3">When to Use</h3>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                Understand company structure and hierarchy
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                Find team members and their roles
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                Initiate communication with colleagues
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                Plan cross-departmental projects
              </li>
            </ul>
          </div>
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-3">Best Practices</h3>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start">
                <span className="text-blue-500 mr-2">•</span>
                Use the search function to find specific people
              </li>
              <li className="flex items-start">
                <span className="text-blue-500 mr-2">•</span>
                Check online status before messaging
              </li>
              <li className="flex items-start">
                <span className="text-blue-500 mr-2">•</span>
                Review department compositions regularly
              </li>
              <li className="flex items-start">
                <span className="text-blue-500 mr-2">•</span>
                Use direct messaging for urgent communications
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-6 bg-blue-50 border-l-4 border-blue-400 p-4">
          <div className="flex">
            <UserGroupIcon className="h-5 w-5 text-blue-400 mr-2" />
            <div>
              <p className="text-sm text-blue-700">
                <strong>Team Collaboration:</strong> This organizational view serves as a central hub for team communication and coordination.
                Use it to foster cross-departmental relationships and maintain clear visibility into your company's structure.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}